import type { ChatMessage as ChatMessageType } from "schemas/generated/chatMessage";

import React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { formatDuration } from "@v2/utils/format-duration";
import { ArrowUpRight } from "lucide-react";

function getBraintrustLink({
  chatId,
  chatTurnId,
}: {
  chatId: string | null;
  chatTurnId?: string | null;
}): string | null {
  if (!chatId) {
    return null;
  }

  const matchItems = [
    {
      label: chatId,
      text: chatId,
      originType: "btql",
    },
  ];

  if (chatTurnId) {
    matchItems.push({
      label: chatTurnId,
      text: chatTurnId,
      originType: "btql",
    });
  }

  return `https://www.braintrust.dev/app/Replo/p/Replo/logs?search=${encodeURIComponent(
    JSON.stringify({
      match: matchItems,
    }),
  )}`;
}

function getReploAdminLink({
  chatId,
  chatTurnId,
}: {
  chatId: string | null;
  chatTurnId?: string | null;
}): string | null {
  if (!chatId) {
    return null;
  }
  const chatTurnIdParam = chatTurnId ? `&chatTurnId=${chatTurnId}` : "";
  return `https://admin.replo.app/dashboard/chats?chatId=${chatId}${chatTurnIdParam}`;
}

function getChatTurnDuration(messages: ChatMessageType[]): string | null {
  if (messages.length === 0) {
    return null;
  }

  const firstMessage = messages[0];
  const lastMessage = messages[messages.length - 1];

  if (!firstMessage || !lastMessage) {
    return null;
  }

  const firstCreatedAt = new Date(firstMessage.createdAt).getTime();
  const lastCreatedAt = new Date(lastMessage.createdAt).getTime();

  const durationSeconds = (lastCreatedAt - firstCreatedAt) / 1000;

  return formatDuration(durationSeconds);
}

export function ChatDebugPanel({
  chatId,
  chatTurnId,
  messages,
  isActivelyStreaming,
}: {
  chatId: string | null;
  chatTurnId?: string | null;
  messages?: ChatMessageType[];
  isActivelyStreaming?: boolean;
}) {
  const braintrustLink = getBraintrustLink({ chatId, chatTurnId });
  const reploAdminLink = getReploAdminLink({ chatId, chatTurnId });
  const duration = getChatTurnDuration(messages ?? []);

  return (
    <div className="flex flex-row gap-2 items-center flex-wrap">
      {braintrustLink && (
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            window.open(braintrustLink, "_blank");
          }}
          endEnhancer={<ArrowUpRight />}
        >
          Braintrust Logs
        </Button>
      )}
      {reploAdminLink && (
        <Button
          variant="primary"
          size="sm"
          onClick={() => {
            window.open(reploAdminLink, "_blank");
          }}
          endEnhancer={<ArrowUpRight />}
        >
          Replo Admin
        </Button>
      )}
      {duration && !isActivelyStreaming && (
        <Tag variant="neutral">Duration: {duration}</Tag>
      )}
    </div>
  );
}
